import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Mail, Phone, MapPin, Users, FileText, Edit, Loader2, Shield } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable, Column } from '../components/common/DataTable';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getCompany, getEmployeesPaginated } from '../api-clients';
import { CompanyDto, EmployeeDto } from '../web-api-client';
import { Company, Employee, RestrictionLevel } from '../types';
import { RestrictionBadge } from '../components/common/RestrictionBadge';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';

// Helper function to map CompanyDto to Company
function mapCompanyDtoToCompany(company: CompanyDto): Company {
    return {
        id: company.id?.toString() || '',
        name: company.companyName || '',
        vatNumber: company.vatNumber || '',
        address: '', // Not available in CarrierDto
        phone: company.contactPhone || '',
        email: company.contactEmail || '',
        employeeCount: company.employeeCount || 0,
        ecmrCount: company.documentCount || 0,
        createdAt: company.created ? new Date(company.created).toISOString() : new Date().toISOString(),
        status: company.isActive ? 'active' : 'inactive',
        restrictionLevel: (company.restrictionLevel ?? 0) as RestrictionLevel,
  };
}

// Helper function to map EmployeeDto to Employee
function mapEmployeeDtoToEmployee(employee: EmployeeDto): Employee {
  return {
    id: employee.id?.toString() || '',
    name: employee.fullName || '',
    email: employee.email || '',
    phone: employee.phoneNumber || '',
    role: employee.role,
    companyId: employee.companyId?.toString() || '',
    companyName: employee.companyName || 'N/A',
    status: employee.isActive ? 'active' : 'inactive',
    createdAt: employee.createdAt ? new Date(employee.createdAt).toISOString() : new Date().toISOString(),
    walletConfigured: !!employee.walletAddress,
  };
}

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [company, setCompany] = useState<Company | null>(null);
  const [companyEmployees, setCompanyEmployees] = useState<Employee[]>([]);
  const [companyDto, setCompanyDto] = useState<CompanyDto | null>(null);
  const [companyEmployeesDto, setCompanyEmployeesDto] = useState<EmployeeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('ID da empresa não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const companyId = parseInt(id, 10);
        if (isNaN(companyId)) {
          throw new Error('ID inválido');
        }

        // Load company
        const companyDtoData = await getCompany(companyId);
        setCompanyDto(companyDtoData);
        setCompany(mapCompanyDtoToCompany(companyDtoData));

        // Load employees for this company
        const employeesResult = await getEmployeesPaginated({
          companyId: companyId,
          page: 1,
          pageSize: 100, // Get all employees for this company
        });
        const employeesDto = employeesResult.items || [];
        setCompanyEmployeesDto(employeesDto);
        const employees = employeesDto.map(mapEmployeeDtoToEmployee);
        setCompanyEmployees(employees);
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Erro ao carregar dados da empresa');
        setError(errorToast.description);
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{error || 'Empresa não encontrada'}</p>
        <Button variant="link" onClick={() => navigate('/companies')}>
          Voltar às empresas
        </Button>
      </div>
    );
  }

  const employeeColumns: Column<Employee>[] = [
    {
      key: 'name',
      header: 'Nome',
      render: (employee) => (
        <div>
          <p className="font-medium">{employee.name}</p>
          <p className="text-sm text-muted-foreground">{employee.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Função',
      render: (employee) => (
        <Badge variant="secondary" className="capitalize">
          {employee.role}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (employee) => (
        <Badge variant={employee.status === 'active' ? 'active' : 'inactive'}>
          {employee.status === 'active' ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/companies')}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <PageHeader
        title={company.name}
        description={company.vatNumber}
        actions={
          <Button 
            variant="outline" 
            onClick={() => navigate(`/companies/${id}/edit`, {
              state: {
                company: companyDto,
                employees: companyEmployeesDto,
              }
            })}
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Company Info */}
        <Card className="lg:col-span-2 animate-slide-up">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-accent" />
              Informação da Empresa
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {company.address && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Morada</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p>{company.address}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Contactos</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{company.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{company.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="space-y-4">
          <Card className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{company.employeeCount}</p>
                  <p className="text-sm text-muted-foreground">Colaboradores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="animate-slide-up" style={{ animationDelay: '200ms' }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                  <FileText className="h-6 w-6 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{company.ecmrCount}</p>
                  <p className="text-sm text-muted-foreground">eCMRs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="animate-slide-up" style={{ animationDelay: '300ms' }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <RestrictionBadge level={company.restrictionLevel ?? 0} />
                  <p className="text-sm text-muted-foreground mt-1">Nível de Restrição</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Employees List */}
      <div className="animate-slide-up" style={{ animationDelay: '300ms' }}>
        <h2 className="text-lg font-semibold mb-4">Colaboradores da Empresa</h2>
        <DataTable
          columns={employeeColumns}
          data={companyEmployees}
          onRowClick={(employee) => navigate(`/employees/${employee.id}`)}
          emptyMessage="Nenhum colaborador associado"
        />
      </div>
    </div>
  );
}
